import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import WeatherCard from "@/components/layout/WeatherCard";
import { formatXAF, formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ResponsiveContainer, Tooltip, PieChart, Pie, Legend, Cell,
} from "recharts";
import {
  Sprout, Fish, Package, Users, TrendingUp, Wheat, AlertTriangle, MapPin,
  Scale, Droplets, Skull, ShoppingCart, HandCoins, Banknote, Bell,
} from "lucide-react";

const METHOD_COLORS = { orange_money: "#CC5500", mtn_mobile_money: "#DDA15E", virement: "#3A86FF", cash: "#4A7C59" };const LEVEL_STYLE = {
  red: { dot: "bg-destructive", text: "text-destructive" },
  orange: { dot: "bg-[#CC5500]", text: "text-[#CC5500]" },
  green: { dot: "bg-[#4A7C59]", text: "text-[#4A7C59]" },
};

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 card-hover" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="w-4 h-4" />
        <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
      </div>
      <div className="mt-2 text-2xl font-bold tracking-tight">{value}</div>
    </div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
          <Icon className="w-4 h-4" />
        </div>
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export default function Dashboard() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-6">Loading…</div>;

  const { kpis } = data;
  const revenueData = Object.entries(data.revenue_by_method || {}).map(([k, v]) => ({
    name: t(k) || k, value: v, key: k,
  }));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <div className="label-eyebrow mb-2 flex items-center gap-2">
          <MapPin className="w-3 h-3" /> AgriNova
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{t("dashboard")}</h1>
        <p className="text-muted-foreground mt-1">{t("key_indicators")} — {t("slogan")}</p>
      </div>

      {/* Alertes cockpit + Météo */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {data.alerts && data.alerts.length > 0 && (
        <Card className="border-border lg:col-span-2" data-testid="alerts-panel">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Bell className="w-4 h-4 text-primary" />
              <h3 className="font-semibold">{t("alerts_title")}</h3>
              <Badge variant="secondary">{data.alerts.length}</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              {data.alerts.map((a, i) => {
                const s = LEVEL_STYLE[a.level] || LEVEL_STYLE.green;
                return (
                  <div key={i} className="flex items-start gap-2 text-sm" data-testid={`alert-${i}`}>
                    <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${s.dot}`} />
                    <span className={s.text}>{a.message}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
        <WeatherCard />
      </div>

      <Section icon={Sprout} title={t("agri_kpis")} testid="section-agri">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 stagger">
          <Kpi icon={Sprout} label={t("hectares")} value={kpis.agri.total_ha} testid="kpi-hectares" />
          <Kpi icon={MapPin} label={t("active_plots")} value={kpis.agri.parcelles_actives} testid="kpi-plots" />
          <Kpi icon={Wheat} label={t("ongoing_crops")} value={kpis.agri.cultures_en_cours} testid="kpi-crops" />
          <Kpi icon={Wheat} label={t("cultures_maturing")} value={kpis.agri.cultures_maturite} testid="kpi-maturing" />
          <Kpi icon={Package} label={t("production_harvested")} value={kpis.agri.production_recoltee} testid="kpi-harvested" />
        </div>
      </Section>

      <Section icon={Fish} title={t("pisci_kpis")} testid="section-pisci">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 stagger">
          <Kpi icon={Fish} label={t("active_ponds")} value={kpis.pisci.bassins} testid="kpi-ponds" />
          <Kpi icon={Fish} label={t("fish_alive")} value={kpis.pisci.poissons_elevage} testid="kpi-fish" />
          <Kpi icon={Scale} label={t("biomass_kg")} value={`${kpis.pisci.biomasse_kg} kg`} testid="kpi-biomass" />
          <Kpi icon={TrendingUp} label={t("avg_fcr")} value={kpis.pisci.fcr_moyen ?? "—"} testid="kpi-fcr" />
          <Kpi icon={Wheat} label={t("feed_30d")} value={`${kpis.pisci.aliment_30j} kg`} testid="kpi-feed" />
          <Kpi icon={Skull} label={t("mortality_30d")} value={kpis.pisci.mortalite_30j} testid="kpi-mortality" />
        </div>
      </Section>

      <Section icon={ShoppingCart} title={t("business_kpis")} testid="section-business">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 stagger">
          <Kpi icon={TrendingUp} label={t("quarter_revenue")} value={formatXAF(kpis.business.ca_trimestre)} testid="kpi-revenue" />
          <Kpi icon={ShoppingCart} label={t("orders_total")} value={kpis.business.commandes} testid="kpi-orders" />
          <Kpi icon={Banknote} label={t("sales_today")} value={formatXAF(kpis.business.ventes_jour)} testid="kpi-sales" />
          <Kpi icon={HandCoins} label={t("receivables")} value={formatXAF(kpis.business.creances)} testid="kpi-receivables" />
          <Kpi icon={AlertTriangle} label={t("out_of_stock")} value={kpis.business.ruptures} testid="kpi-lowstock" />
        </div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-border lg:col-span-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t("upcoming_harvests")}</h3>
              <Wheat className="w-4 h-4 text-primary" />
            </div>
            {data.upcoming_harvests.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">{t("no_data")}</div>
            ) : (
              <div className="space-y-2">
                {data.upcoming_harvests.map((p) => (
                  <div key={p.id} className="flex items-center justify-between border-b border-border/60 last:border-0 py-2">
                    <div>
                      <div className="font-medium">{p.culture_name}</div>
                      <div className="text-xs text-muted-foreground">{p.parcelle_name} · {formatDate(p.expected_harvest_date, lang)}</div>
                    </div>
                    <Badge variant={p.days_until < 15 ? "destructive" : "secondary"}>
                      {p.days_until} {t("days")}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">{t("revenue_by_method")}</h3>
            {revenueData.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">{t("no_data")}</div>
            ) : (
              <div className="h-56">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={revenueData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70}>
                      {revenueData.map((e, i) => (
                        <Cell key={i} fill={METHOD_COLORS[e.key] || "#1E3F20"} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatXAF(v)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t("low_stock")}</h3>
              <AlertTriangle className="w-4 h-4 text-[#CC5500]" />
            </div>
            {data.low_stock_items.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">{t("no_data")}</div>
            ) : (
              <div className="space-y-2">
                {data.low_stock_items.map((s) => (
                  <div key={s.id} className="flex items-center justify-between border-b border-border/60 last:border-0 py-2">
                    <div>
                      <div className="font-medium">{s.name}</div>
                      <div className="text-xs text-muted-foreground">{s.category}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{s.quantity} {s.unit}</div>
                      <div className="text-xs text-muted-foreground">min {s.min_threshold}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">{t("recent_payments")}</h3>
            {data.recent_payments.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">{t("no_data")}</div>
            ) : (
              <div className="space-y-2">
                {data.recent_payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between border-b border-border/60 last:border-0 py-2">
                    <div>
                      <div className="font-medium">{p.customer_name}</div>
                      <div className="text-xs text-muted-foreground">{t(p.method)} · {formatDate(p.date, lang)}</div>
                    </div>
                    <div className="font-semibold text-primary">{formatXAF(p.amount)}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
